// frontend/src/components/landing-page/pages/GetStarted.jsx
import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link, useNavigate } from 'react-router-dom';
import { FiEye, FiEyeOff, FiHome, FiUser, FiCheck } from 'react-icons/fi';
import poslahLogo from '../../../assets/poslahlogo.png';
import { countryService } from '../../../services/countryService';
import { subscriptionService } from '../../../services/subscriptionService';
import '../../../css/auth-pages.css';

const MotionDiv = motion.div;
const MotionImg = motion.img;
const MotionButton = motion.button;

const GetStarted = () => {
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [countries, setCountries] = useState([]);
  const [subscriptionPlans, setSubscriptionPlans] = useState([]);
  const [currentStep, setCurrentStep] = useState(1);
  const [errors, setErrors] = useState({});

  const [formData, setFormData] = useState({
    vendorName: '',
    contactName: '',
    contactNumber: '',
    countryId: '',
    locationName: '',
    locationAddress: '',
    userName: '',
    email: '',
    password: '',
    confirmPassword: '',
    planId: '',
    agreeToTerms: false
  });

  useEffect(() => {
    loadCountries();
    loadSubscriptionPlans();
  }, []);

  const loadCountries = async () => {
    try {
      const data = await countryService.getAllCountries();
      setCountries(data);
    } catch (error) {
      console.error('Failed to load countries:', error);
    }
  };

  const loadSubscriptionPlans = async () => {
    try {
      const data = await subscriptionService.getAllPlans();
      setSubscriptionPlans(data);
    } catch (error) {
      console.error('Failed to load subscription plans:', error);
    }
  };

  const getSelectedCountryData = () => {
    return countries.find(country => country.country_id === parseInt(formData.countryId));
  };

  const handleCountryChange = (e) => {
    const countryId = e.target.value;
    setFormData(prev => ({ ...prev, contactNumber: '', countryId }));
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value
    });
    
    if (errors[name]) {
      setErrors({ ...errors, [name]: '' });
    }
  };

  const validateStep = (step) => {
    const newErrors = {};
    
    if (step === 1) {
      if (!formData.vendorName.trim()) newErrors.vendorName = 'Business name is required';
      if (!formData.contactName.trim()) newErrors.contactName = 'Contact name is required';
      if (!formData.countryId) newErrors.countryId = 'Please select a country';
      if (!formData.contactNumber.trim()) newErrors.contactNumber = 'Contact number is required';
      if (!formData.locationName.trim()) newErrors.locationName = 'Location name is required';
      if (!formData.locationAddress.trim()) newErrors.locationAddress = 'Address is required';
    }
    
    if (step === 2) {
      if (!formData.userName.trim()) newErrors.userName = 'Username is required';
      if (!formData.email.trim()) newErrors.email = 'Email is required';
      if (!/\S+@\S+\.\S+/.test(formData.email)) newErrors.email = 'Email is invalid';
      if (!formData.password) newErrors.password = 'Password is required';
      if (formData.password.length < 8) newErrors.password = 'Password must be at least 8 characters';
      if (formData.password !== formData.confirmPassword) newErrors.confirmPassword = 'Passwords do not match';
    }
    
    if (step === 3) {
      if (!formData.planId) newErrors.planId = 'Please select a subscription plan';
      if (!formData.agreeToTerms) newErrors.agreeToTerms = 'You must agree to the terms and conditions';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const nextStep = () => {
    if (validateStep(currentStep)) {
      setCurrentStep(currentStep + 1);
    }
  };

  const prevStep = () => {
    setCurrentStep(currentStep - 1);
    setErrors({});
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateStep(3)) return;
    setLoading(true);
    
    try {
      // Just collect data, don't register yet - registration happens after payment
      const selectedPlan = subscriptionPlans.find(p => p.plan_id === parseInt(formData.planId));
      const selectedCountry = countries.find(c => c.country_id === parseInt(formData.countryId));
      
      // Navigate to payment with all data
      navigate('/payment-info', { 
        state: { 
          plan: selectedPlan,
          country: selectedCountry,
          registrationData: {
            vendorName: formData.vendorName,
            contactName: formData.contactName,
            contactNumber: formData.contactNumber,
            countryId: formData.countryId,
            locationName: formData.locationName,
            locationAddress: formData.locationAddress,
            userName: formData.userName,
            email: formData.email,
            password: formData.password,
            planId: formData.planId
          }
        }
      });
    } catch (err) {
      console.error('Navigation error:', err);
      setErrors({ submit: 'Something went wrong. Please try again.' });
    } finally {
      setLoading(false);
    }
  };

  const containerVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 }
  };

  const renderStep1 = () => (
    <MotionDiv className="form-section" variants={itemVariants}>
      <h3 className="section-title">
        <FiHome className="section-icon" />
        Business Information
      </h3>
      <div className="form-grid">
        <div className="form-group full-width">
          <input
            type="text"
            name="vendorName"
            className={`form-input ${errors.vendorName ? 'error' : ''}`}
            placeholder="Company/Business Name *"
            value={formData.vendorName}
            onChange={handleInputChange}
          />
          {errors.vendorName && <span className="error-text">{errors.vendorName}</span>}
        </div>
        
        <div className="form-group">
          <input
            type="text"
            name="contactName"
            className={`form-input ${errors.contactName ? 'error' : ''}`}
            placeholder="Contact Person Name *"
            value={formData.contactName}
            onChange={handleInputChange}
          />
          {errors.contactName && <span className="error-text">{errors.contactName}</span>}
        </div>
        
        <div className="form-group">
          <select
            name="countryId"
            className={`form-input ${errors.countryId ? 'error' : ''}`}
            value={formData.countryId}
            onChange={handleCountryChange}
          >
            <option value="">Select Country *</option>
            {countries.map(country => (
              <option key={country.country_id} value={country.country_id}>
                {country.country_name}
              </option>
            ))}
          </select>
          {errors.countryId && <span className="error-text">{errors.countryId}</span>}
        </div>
        
        <div className="form-group full-width">
          <div className="phone-input-group">
            <div className="country-code">
              {getSelectedCountryData()?.phone_code || '+--'}
            </div>
            <input
              type="tel"
              name="contactNumber"
              className={`form-input phone-input ${errors.contactNumber ? 'error' : ''}`}
              placeholder="Contact Number *"
              value={formData.contactNumber}
              onChange={handleInputChange}
            />
          </div>
          {errors.contactNumber && <span className="error-text">{errors.contactNumber}</span>}
        </div>
        
        <div className="form-group full-width">
          <input
            type="text"
            name="locationName"
            className={`form-input ${errors.locationName ? 'error' : ''}`}
            placeholder="Main Location/Branch Name *"
            value={formData.locationName}
            onChange={handleInputChange}
          />
          {errors.locationName && <span className="error-text">{errors.locationName}</span>}
        </div>
        
        <div className="form-group full-width">
          <textarea
            name="locationAddress"
            className={`form-input ${errors.locationAddress ? 'error' : ''}`}
            placeholder="Business Address *"
            value={formData.locationAddress}
            onChange={handleInputChange}
            rows={3}
          />
          {errors.locationAddress && <span className="error-text">{errors.locationAddress}</span>}
        </div>
      </div>
    </MotionDiv>
  );

  const renderStep2 = () => (
    <MotionDiv className="form-section" variants={itemVariants}>
      <h3 className="section-title">
        <FiUser className="section-icon" />
        Account Information
      </h3>
      <div className="form-grid">
        <div className="form-group">
          <input
            type="text"
            name="userName"
            className={`form-input ${errors.userName ? 'error' : ''}`}
            placeholder="Username *"
            value={formData.userName}
            onChange={handleInputChange}
          />
          {errors.userName && <span className="error-text">{errors.userName}</span>}
        </div>
        
        <div className="form-group">
          <input
            type="email"
            name="email"
            className={`form-input ${errors.email ? 'error' : ''}`}
            placeholder="Email Address *"
            value={formData.email}
            onChange={handleInputChange}
          />
          {errors.email && <span className="error-text">{errors.email}</span>}
        </div>
        
        <div className="form-group full-width password-group">
          <input
            type={showPassword ? "text" : "password"}
            name="password"
            className={`form-input ${errors.password ? 'error' : ''}`}
            placeholder="Password *"
            value={formData.password}
            onChange={handleInputChange}
          />
          <button 
            type="button"
            className="password-toggle"
            onClick={() => setShowPassword(!showPassword)}
          >
            {showPassword ? <FiEyeOff /> : <FiEye />}
          </button>
          {errors.password && <span className="error-text">{errors.password}</span>}
        </div>
        
        <div className="form-group full-width password-group">
          <input
            type={showPassword ? "text" : "password"}
            name="confirmPassword"
            className={`form-input ${errors.confirmPassword ? 'error' : ''}`}
            placeholder="Confirm Password *"
            value={formData.confirmPassword}
            onChange={handleInputChange}
          />
          {errors.confirmPassword && <span className="error-text">{errors.confirmPassword}</span>}
        </div>
      </div>
    </MotionDiv>
  );

  const renderStep3 = () => (
    <MotionDiv className="form-section" variants={itemVariants}>
      <h3 className="section-title">
        <FiCheck className="section-icon" />
        Choose Your Plan
      </h3>
      
      <div className="pricing-plans-grid">
        {subscriptionPlans.map(plan => (
          <div 
            key={plan.plan_id}
            className={`pricing-plan-card ${formData.planId === plan.plan_id.toString() ? 'selected' : ''} ${plan.plan_name === 'Pro' ? 'recommended' : ''}`}
            onClick={() => setFormData({ ...formData, planId: plan.plan_id.toString() })}
          >
            {plan.plan_name === 'Pro' && <div className="recommended-badge">Recommended</div>}
            
            <div className="plan-header">
              <h4 className="plan-name">{plan.plan_name}</h4>
              <div className="plan-price">
                {plan.plan_type === 'free' ? (
                  <span className="price">Free</span>
                ) : (
                  <>
                    <span className="currency">{getSelectedCountryData()?.currency_symbol || '$'}</span>
                    <span className="price">{plan.monthly_price}</span>
                    <span className="period">/month</span>
                  </>
                )}
              </div>
              {plan.trial_days > 0 && (
                <div className="trial-info">
                  {plan.trial_days} days free trial
                </div>
              )}
            </div>
            
            <div className="plan-features">
              <div className="feature-item">
                <FiCheck className="check-icon" />
                <span>Up to {plan.max_branches === -1 ? 'Unlimited' : plan.max_branches} branch{plan.max_branches !== 1 ? 'es' : ''}</span>
              </div>
              <div className="feature-item">
                <FiCheck className="check-icon" />
                <span>Up to {plan.max_users === -1 ? 'Unlimited' : plan.max_users} user{plan.max_users !== 1 ? 's' : ''}</span>
              </div>
              <div className="feature-item">
                <FiCheck className="check-icon" />
                <span>Up to {plan.max_products === -1 ? 'Unlimited' : plan.max_products} products</span>
              </div>
              {plan.features && (typeof plan.features === 'string' ? JSON.parse(plan.features) : plan.features).ai_insights && (
                <div className="feature-item">
                  <FiCheck className="check-icon" />
                  <span>AI Insights & Analytics</span>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
      
      {errors.planId && <span className="error-text">{errors.planId}</span>}
      
      <div className="terms-checkbox">
        <label className="checkbox-label">
          <input
            type="checkbox"
            name="agreeToTerms"
            checked={formData.agreeToTerms}
            onChange={handleInputChange}
            className={errors.agreeToTerms ? 'error' : ''}
          />
          <span className="checkmark"></span>
          I agree to the <Link to="/terms" target="_blank">Terms & Conditions</Link> and <Link to="/privacy" target="_blank">Privacy Policy</Link>
        </label>
        {errors.agreeToTerms && <span className="error-text">{errors.agreeToTerms}</span>}
      </div>
    </MotionDiv>
  );

  return (
    <div className="getstarted-page">
      <div className="animated-bg"></div>

      <MotionDiv 
        className="getstarted-container"
        variants={containerVariants}
        initial="hidden"
        animate="visible"
      >
        <MotionDiv className="getstarted-content" variants={itemVariants}>
          {/* Logo Section */}
          <MotionDiv className="logo-section" variants={itemVariants}>
            <Link to="/">
              <MotionImg 
                src={poslahLogo} 
                alt="POSlah Logo"
                className="getstarted-logo"
                whileHover={{ scale: 1.05, rotate: 2 }}
                transition={{ duration: 0.3 }}
              />
            </Link>
          </MotionDiv>

          {/* Multi-step Form Card */}
          <MotionDiv className="getstarted-card" variants={itemVariants}>
            <div className="card-header">
              <h1 className="getstarted-title">Start Your Journey</h1>
              <p className="getstarted-subtitle">
                Set up your POS system in just a few steps
              </p>
              
              {/* Progress Indicator */}
              <div className="progress-indicator">
                <div className={`step ${currentStep >= 1 ? 'active' : ''}`}>
                  <span className="step-number">1</span>
                  <span className="step-label">Business</span>
                </div>
                <div className={`step ${currentStep >= 2 ? 'active' : ''}`}>
                  <span className="step-number">2</span>
                  <span className="step-label">Account</span>
                </div>
                <div className={`step ${currentStep >= 3 ? 'active' : ''}`}>
                  <span className="step-number">3</span>
                  <span className="step-label">Plan</span>
                </div>
              </div>
            </div>

            <form onSubmit={handleSubmit} className="getstarted-form">
              <div 
                className="step-container"
                style={{
                  opacity: 1,
                  transform: 'translateX(0)',
                  transition: 'all 0.3s ease'
                }}
              >
                {currentStep === 1 && renderStep1()}
                {currentStep === 2 && renderStep2()}
                {currentStep === 3 && renderStep3()}
              </div>

              {errors.submit && (
                <div className="error-message">
                  {errors.submit}
                </div>
              )}

              {/* Navigation Buttons */}
              <div className="form-navigation">
                {currentStep > 1 && (
                  <MotionButton
                    type="button"
                    className="nav-btn prev-btn"
                    onClick={prevStep}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    Previous
                  </MotionButton>
                )}
                
                {currentStep < 3 ? (
                  <MotionButton
                    type="button"
                    className="nav-btn next-btn"
                    onClick={nextStep}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    Next Step
                  </MotionButton>
                ) : (
                  <MotionButton
                    type="submit"
                    className="register-btn"
                    disabled={loading}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    {loading ? (
                      <>
                        <div className="loading-spinner"></div>
                        Proceeding to Payment...
                      </>
                    ) : (
                      'Proceed to Payment'
                    )}
                  </MotionButton>
                )}
              </div>
            </form>
            
            <MotionDiv className="getstarted-footer" variants={itemVariants}>
              <p>Already have an account?</p>
              <Link to="/signin" className="signin-link">
                Sign In Here
              </Link>
            </MotionDiv>
          </MotionDiv>
        </MotionDiv>
      </MotionDiv>
    </div>
  );
};

export default GetStarted;